import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { CURRENCIES, type Category, type Currency, type PlanGroup, type PlanKind } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { parseAmount } from "../../shared/lib/format";
import { ChipGroup } from "../../shared/ui/ChipGroup";
import { DateField } from "../../shared/ui/DateField";
import { Modal } from "../../shared/ui/Modal";
import { Select } from "../../shared/ui/Select";
import { createPlanItem, setPlanItemGroup, type PlanningPeriod } from "./api";

interface GroupCreateItemDialogProps {
  open: boolean;
  onClose: () => void;
  bookID: string;
  group: PlanGroup;
  baseCurrency: Currency;
  period: PlanningPeriod;
  categories: Category[];
  onNotify: (message: string) => void;
  onChanged: () => Promise<void>;
}

// A new plan item, filed straight into this group. It is an ordinary atomic
// item: one amount, one currency. The group is where it is filed, not a second
// place the money lives.
export function GroupCreateItemDialog({
  open, onClose, bookID, group, baseCurrency, period, categories, onNotify, onChanged,
}: GroupCreateItemDialogProps) {
  const [form, setForm] = useState({
    kind: "expense" as PlanKind, name: "", amount: "", currency: baseCurrency, categoryId: "", dueDate: "",
  });
  const [refused, setRefused] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      const created = await createPlanItem(bookID, {
        ...form, amount: parseAmount(form.amount) ?? "", periodStart: period.start, periodEnd: period.end,
      });
      // Created first, then filed: the item exists on its own terms and the
      // group is a separate, reversible fact about it.
      await setPlanItemGroup(bookID, created.planItem.id, group.id);
      return created;
    },
    onSuccess: async (created) => {
      setForm({ kind: "expense", name: "", amount: "", currency: baseCurrency, categoryId: "", dueDate: "" });
      await onChanged();
      onNotify(`"${created.planItem.name}" added to "${group.name}".`);
      onClose();
    },
  });

  return (
    <Modal
      open={open} onClose={onClose} title="Create a plan item" subtitle={`It will be filed in "${group.name}".`}
      footer={<>
        <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
        <button className="primary-button compact" type="submit" form={`create-in-${group.id}`} disabled={create.isPending}>
          <span>{create.isPending ? "Creating…" : "Create and add"}</span><span aria-hidden="true">→</span>
        </button>
      </>}
    >
      <form id={`create-in-${group.id}`} className="stacked-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!form.name.trim() || parseAmount(form.amount) === null) {
            setRefused("A plan item needs a name and an amount.");
            return;
          }
          setRefused("");
          create.mutate();
        }}>
        <div className="field">
          <span className="field-label">Kind</span>
          <ChipGroup label="Plan kind" value={form.kind}
            onChange={(kind) => setForm({ ...form, kind: kind as PlanKind })}
            options={[{ value: "expense", label: "Expense" }, { value: "income", label: "Income" }]} />
        </div>
        <div className="field-grid">
          <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Flights" required autoFocus /></label>
          <label>Amount<span className="amount-input">
            <input value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} inputMode="decimal" placeholder="1200.00" required />
            <Select label="Currency" value={form.currency}
              onChange={(currency) => setForm({ ...form, currency: currency as Currency })}
              options={CURRENCIES.map((value) => ({ value, label: value }))} />
          </span></label>
          <div className="field">
            <span className="field-label" id={`create-category-${group.id}`}>Category</span>
            <Select labelledBy={`create-category-${group.id}`} value={form.categoryId} placeholder="— none —"
              onChange={(categoryId) => setForm({ ...form, categoryId })}
              options={[{ value: "", label: "— none —" }, ...categories.map((category) => ({ value: category.id, label: category.name }))]} />
          </div>
          <DateField label="Due date" value={form.dueDate} min={period.start.slice(0, 10)}
            onChange={(dueDate) => setForm({ ...form, dueDate })} />
        </div>
        <p className="panel-note">One amount in one currency. A cost in another currency is another plan item; the group may hold both.</p>
        {refused ? <p className="form-error" role="alert">{refused}</p> : null}
        {create.error ? <p className="form-error" role="alert">{humanError(create.error)}</p> : null}
      </form>
    </Modal>
  );
}
